'use client';
import type { StateCreator } from 'zustand';
import type { ChangePreviewViewDataType, OrderInfo, PaymentInfoType, PayStatustype, ProductType } from '@/app/types';

// 支付状态接口
interface PaymentState {
  payStatus: PayStatustype; // 订单状态（包含loading状态）
  orderInfo?: OrderInfo; // 订单信息
  payProduct?: ProductType; // 当前购买产品
  payQuantity?: number; // 当前购买数量（仅 claw_pass_bundle 等支持数量的场景使用）
  /**
   * 当前购买时用户选择的优惠券 id：传 string 指定该券，传 null/undefined 表示不使用优惠券。
   * 用于变更流程跨组件传递（StripeConfirmDialog 选券 → fetchChangePreview → ChangeContent → changeSubscription）
   */
  payCouponId?: string | null;
  /** True when purchasing from the paid-trial tab. */
  isPaidTrialPurchase?: boolean;
  /**
   * 发起订阅变更时用户是否仍处于付费试用期（快照）。
   * 变更成功后 subscription 会被重新拉取，is_paid_trialing 可能已翻转，
   * 故在 pay() 时固化，供结果页展示「从 试用套餐」使用。
   */
  paidTrialingAtChange?: boolean;
  confirmedChangeData?: ChangePreviewViewDataType; // 确认变更信息
  paymentInfo?: PaymentInfoType; // 支付详情
  paymentPopupState: boolean; // 是否打开支付弹窗
  refreshGetProducts: boolean; // 是否刷新获取产品列表
  couponSummary: { unusedCount: number; nearestUnusedExpireAt: string | null } | null; // 优惠券概览（banner 与 dialog 共享）
  maxProductDiscount: number; // 当前站点各购买周期中的最大折扣百分比
  /**
   * 商品列表里是否存在付费试用档（null 表示尚未拉取）。
   * 后端只对符合资格的用户下发 `paid_trial`，故它同时代表"订阅页会出现试用 tab"。
   * 消费方需区分 null 与 false：前者是"还没拉到"，后者是"确认没有试用档"。
   */
  hasPaidTrialProduct: boolean | null;
}

export interface PayStore extends PaymentState {
  // 关闭支付弹窗
  closePayment: (callback?: (status: PayStatustype) => void) => void;
  // 二维码超时
  handleQrcodeTimeout: () => void;
  // 状态更新方法
  updatePaymentState: (updates: Partial<PaymentState>) => void;
  // 重置支付状态
  resetPaymentState: () => void;
  // 更新获取产品列表
  updateRefreshGetProducts: (value: boolean) => void;
  updateCouponSummary: (value: PaymentState['couponSummary']) => void;
  updateMaxProductDiscount: (value: number) => void;
  updateHasPaidTrialProduct: (value: boolean | null) => void;
}

const initialState: PaymentState = {
  payStatus: 'unspecified',
  paymentPopupState: false,
  refreshGetProducts: false,
  couponSummary: null,
  maxProductDiscount: 0,
  hasPaidTrialProduct: null,
};

export const createPaySlice: StateCreator<PayStore, [], [], PayStore> = (set, get) => ({
  ...initialState,

  // 更新支付状态
  updatePaymentState: (updates) => {
    set((state) => ({ ...state, ...updates }));
  },

  // 关闭支付弹窗
  closePayment: (callback) => {
    const state = get();
    set({
      payStatus: 'unspecified',
      paymentPopupState: false,
      payQuantity: undefined,
      payCouponId: undefined,
      isPaidTrialPurchase: undefined,
      paidTrialingAtChange: undefined,
    });
    // 执行回调
    callback ? callback(state.payStatus) : null;
  },

  // 二维码超时
  handleQrcodeTimeout: () => {
    set({
      payStatus: 'closed',
    });
  },

  // 重置支付状态
  resetPaymentState: () => {
    set({ ...initialState, paymentPopupState: false });
  },

  // 获取产品列表
  updateRefreshGetProducts: (value: boolean) => {
    set({ refreshGetProducts: value });
  },

  updateCouponSummary: (value) => {
    set({ couponSummary: value });
  },

  updateMaxProductDiscount: (value: number) => {
    set({ maxProductDiscount: value });
  },

  updateHasPaidTrialProduct: (value: boolean | null) => {
    set({ hasPaidTrialProduct: value });
  },
});
